import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Navigate, useLocation, useOutlet } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { cn } from "@/lib/utils";
import PublicSidebar from "@/components/public/PublicSidebar";
import MobileHeader from "@/components/mobile/MobileHeader";
import MobileBottomNav, { getMobileNativeTabKey, MOBILE_NATIVE_TABS } from "@/components/mobile/MobileBottomNav";
import NativeSelectMobileSheet from "@/components/mobile/NativeSelectMobileSheet";
import PageTransition from "@/components/mobile/PageTransition";
import PullToRefresh from "@/components/mobile/PullToRefresh";
import RouteLoading from "@/components/layout/RouteLoading";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ChatBot from "@/components/chat/ChatBot";
import OnboardingWizard from "@/components/profile/OnboardingWizard";
import GlobalFeedbackButton from "@/components/feedback/GlobalFeedbackButton";
import CookieConsentBanner from "@/components/privacy/CookieConsentBanner";
import GoogleAnalyticsRouteTracker from "@/components/analytics/GoogleAnalyticsRouteTracker";
import Footer from "./Footer";
import { isPathHidden } from "@/lib/navigationVisibility";
import { SUPPORTED_LANGUAGES } from "@/lib/i18nRoutes";
import { getShellCopy } from "@/lib/shellTranslations";

import { useLanguage } from "@/lib/LanguageContext";
import PublicAuthButton from "@/components/public/PublicAuthButton";
import { PublicThemeToggle } from "@/components/public/PublicThemeController";
import { useIsMobile } from "@/hooks/use-mobile";
import { getMobileNavigationStackDepth } from "@/lib/mobileNavigation";

export default function AppLayout() {
  const [user, setUser] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [layoutMode, setLayoutMode] = useState(localStorage.getItem("navLayout") || "side");
  const [hiddenPages, setHiddenPages] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hiddenPages") || "[]"); } catch { return []; }
  });
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const pathLang = location.pathname.split("/").filter(Boolean)[0];
  const activeLanguage = SUPPORTED_LANGUAGES.includes(pathLang) ? pathLang : language;
  const shell = getShellCopy(activeLanguage || "en");
  const isRTL = activeLanguage === "he" || activeLanguage === "ar";
  const isNavigationPage = /\/navigation\/?$/.test(location.pathname);
  const isMobile = useIsMobile();
  const outlet = useOutlet({ user });
  const activeNativeTabKey = getMobileNativeTabKey(location.pathname);
  const reducedMotion = useReducedMotion();
  const previousStackDepthRef = useRef(getMobileNavigationStackDepth(location.pathname, activeLanguage));
  const [mobileTabPanels, setMobileTabPanels] = useState({});
  const stackDepth = getMobileNavigationStackDepth(location.pathname, activeLanguage);
  const transitionDirection = stackDepth >= previousStackDepthRef.current ? 1 : -1;

  useEffect(() => {
    previousStackDepthRef.current = stackDepth;
  }, [location.pathname, stackDepth]);

  useEffect(() => {
    if (!isMobile || !activeNativeTabKey || !outlet) return;
    setMobileTabPanels((panels) => ({ ...panels, [activeNativeTabKey]: outlet }));
  }, [isMobile, activeNativeTabKey, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (SUPPORTED_LANGUAGES.includes(pathLang) && pathLang !== language) setLanguage(pathLang);
  }, [pathLang, language, setLanguage]);

  useEffect(() => {
    base44.auth.me().then(u => {
      setUser(u);
      if (u && !u.onboarded) {
        setShowWelcome(true);
      }
      base44.entities.NavigationVisibilitySetting.filter({ key: "global" }).then(records => {
        const saved = records[0]?.hidden_pages || [];
        setHiddenPages(saved);
        localStorage.setItem("hiddenPages", JSON.stringify(saved));
        window.dispatchEvent(new Event("hiddenPagesChanged"));
      });
      // Browser GPS is used by location-aware pages; no IP location detection needed.
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleLayoutChange = () => {
      setLayoutMode(localStorage.getItem("navLayout") || "side");
    };
    const handleHiddenPagesChange = () => {
      try { setHiddenPages(JSON.parse(localStorage.getItem("hiddenPages") || "[]")); } catch { setHiddenPages([]); }
    };
    window.addEventListener("navLayoutChanged", handleLayoutChange);
    window.addEventListener("hiddenPagesChanged", handleHiddenPagesChange);
    return () => {
      window.removeEventListener("navLayoutChanged", handleLayoutChange);
      window.removeEventListener("hiddenPagesChanged", handleHiddenPagesChange);
    };
  }, []);

  const isBillingRoute = /^\/billing(\/|$)/.test(location.pathname);
  const effectiveLayoutMode = "side";

  if (!isBillingRoute && user?.role !== "admin" && isPathHidden(location.pathname, hiddenPages)) {
    return <Navigate to="/en/" replace />;
  }

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className={cn("relative min-h-screen overflow-x-hidden bg-background", isNavigationPage && "h-[100dvh] overflow-hidden")}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-slate-950 focus:px-4 focus:py-3 focus:text-sm focus:font-bold focus:text-white">{shell.skipToMainContent}</a>
      <GoogleAnalyticsRouteTracker />
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_18%_8%,hsl(var(--accent)/0.18),transparent_30rem),radial-gradient(circle_at_82%_0%,hsl(var(--seafoam)/0.14),transparent_28rem),linear-gradient(180deg,hsl(var(--background)),hsl(var(--background)/0.92))]" />
      <div className="pointer-events-none fixed inset-0 -z-10 maritime-wave-pattern opacity-30" />
      {!isMobile && (
        <div
          className="fixed inset-y-0 z-[90] hidden lg:block"
          style={isRTL ? { right: 0, left: "auto", width: collapsed ? "5rem" : "16rem" } : { left: 0, right: "auto", width: collapsed ? "5rem" : "16rem" }}
        >
          <PublicSidebar lang={activeLanguage || "en"} collapsed={collapsed} onPremiumClick={() => { window.location.href = `/${activeLanguage || "en"}/premium`; }} />
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="absolute top-20 grid h-8 w-8 place-items-center rounded-full border border-slate-200 bg-white text-xs font-black text-slate-700 shadow-lg hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/45 dark:border-cyan-100/70 dark:bg-cyan-50 dark:text-slate-950 dark:hover:bg-white"
            style={isRTL ? { left: "-0.75rem", right: "auto" } : { right: "-0.75rem", left: "auto" }}
            aria-label={shell.collapseSidebar}
          >
            {collapsed ? (isRTL ? "‹" : "›") : (isRTL ? "›" : "‹")}
          </button>
        </div>
      )}
      {isMobile && <MobileHeader />}
      {!isNavigationPage && (
        <div className="hidden md:block fixed left-1/2 top-[calc(0.75rem+env(safe-area-inset-top,0px))] z-[80] -translate-x-1/2">
          <LanguageSwitcher />
        </div>
      )}
      <main id="main-content" className={cn(
        isNavigationPage ? "h-[100dvh] min-h-0 w-full min-w-0 overflow-hidden overscroll-none transition-all duration-300 flex flex-col" : "min-h-screen w-full min-w-0 overflow-x-hidden overscroll-none transition-all duration-300 flex flex-col motion-reduce:transition-none",
        effectiveLayoutMode === "top" 
          ? "pt-16" 
          : isRTL 
            ? (collapsed ? "lg:pr-20" : "lg:pr-64")
            : (collapsed ? "lg:pl-20" : "lg:pl-64"),
        isNavigationPage ? "md:pt-0 pt-[calc(5.25rem+env(safe-area-inset-top,0px))] pb-[calc(7.25rem+env(safe-area-inset-bottom,0px))] md:pb-0 overflow-hidden bg-transparent" : "md:pt-0 pt-[calc(5.25rem+env(safe-area-inset-top,0px))] pb-[calc(7.75rem+env(safe-area-inset-bottom,0px))] md:pb-0 overflow-x-hidden bg-transparent"
      )}>
        <div className={cn("flex-1 min-w-0 overflow-x-hidden overscroll-none", isNavigationPage && "min-h-0 overflow-hidden")}>
          {isNavigationPage ? (
            <>{outlet}</>
          ) : isMobile ? (
            <PullToRefresh>
              <div className="relative w-full min-w-0 overflow-x-hidden">
                {MOBILE_NATIVE_TABS.map((tab) => {
                  const panel = activeNativeTabKey === tab.key ? outlet : mobileTabPanels[tab.key];
                  const active = activeNativeTabKey === tab.key;
                  return (
                    <section
                      key={tab.key}
                      aria-hidden={!active}
                      className={cn(active ? "relative block" : "absolute inset-0 hidden", "w-full min-w-0 overflow-x-hidden")}
                    >
                      {panel && (
                        <AnimatePresence mode="wait" initial={false} custom={transitionDirection}>
                          <motion.div
                            key={active ? location.pathname : tab.key}
                            custom={transitionDirection}
                            className="w-full min-w-0 overflow-x-hidden"
                            initial={active && !reducedMotion ? { x: transitionDirection > 0 ? 32 : -32, opacity: 0.92 } : false}
                            animate={reducedMotion ? { opacity: 1 } : { x: 0, opacity: 1 }}
                            exit={active && !reducedMotion ? { x: transitionDirection > 0 ? -32 : 32, opacity: 0.92 } : { opacity: 0 }}
                            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                          >
                            <React.Suspense fallback={<RouteLoading />}>
                              {panel}
                            </React.Suspense>
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </section>
                  );
                })}
                {!activeNativeTabKey && (
                  <React.Suspense fallback={<RouteLoading />}>
                    {outlet}
                  </React.Suspense>
                )}
              </div>
            </PullToRefresh>
          ) : (
            <PullToRefresh>
              <PageTransition pageKey={location.pathname}>
                <React.Suspense fallback={<RouteLoading />}>
                  {outlet}
                </React.Suspense>
              </PageTransition>
            </PullToRefresh>
          )}
        </div>
        {!isNavigationPage && <Footer />}
      </main>
      {isMobile && (
        <>
          <MobileBottomNav />
          <NativeSelectMobileSheet />
        </>
      )}
      {user && user.onboarded === false && showWelcome && <OnboardingWizard user={user} onClose={() => setShowWelcome(false)} />}
      {user && (
        <div className={cn(isNavigationPage && "hidden md:block")}>
          <GlobalFeedbackButton user={user} />
        </div>
      )}
      {user && (
        <div className={cn(isNavigationPage && "hidden md:block")}>
          <ChatBot user={user} compactMapPosition={/\/navigation\/?$/.test(location.pathname)} />
        </div>
      )}
      <CookieConsentBanner />
    </div>
  );
}